import { Anchor, Badge, Group, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';

import classes from './AtlasAiHero.module.css';
import CogniteLogo from '#assets/cognite-logo-icon.svg?react';
import aiVideo from '#assets/video/agent/3.mp4?url';

const agentTransition = {
  hidden: { opacity: 0, y: -50, scale: 1.3 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 1.5 } },
  exit: { opacity: 0, y: 0, scale: 1.3, transition: { duration: 0.5 } },
} as const;

const itemTransition = {
  hidden: { opacity: 0, scale: 0.5, y: 50 },
  show: { opacity: 1, scale: 1, y: 0, transition: { duration: 1.5 } },
  exit: { opacity: 0, scale: 1.5, y: 0, transition: { duration: 0.5 } },
} as const;

const defaultChildren = <AtlasAiTitle />;

export function AtlasAiHero({ children = defaultChildren }: React.PropsWithChildren) {
  return (
    <motion.div className={classes.hero} initial="hidden" animate="show" exit="exit" layout>
      <motion.div className={classes.agent} variants={agentTransition}>
        <video className={classes.spinner} autoPlay muted loop playsInline>
          <source src={aiVideo} type="video/mp4" />
        </video>
      </motion.div>

      {children}
    </motion.div>
  );
}

export interface AtlasAiTitleProps {
  title?: string;
  subtitle?: string;
}

export function AtlasAiTitle({
  title = 'Digital Agent',
  subtitle = 'Powered by Cognite Atlas AI™',
}: AtlasAiTitleProps) {
  return (
    <motion.div className={classes.title} variants={itemTransition}>
      {title && <Text size="xl">{title}</Text>}

      {subtitle && (
        <Badge
          gradient={{ from: 'grape', to: 'violet', deg: 120 }}
          leftSection={<CogniteLogo height={24} width={24} fill="white" />}
          size="xl"
          variant="gradient"
          tt="initial"
        >
          {subtitle}
        </Badge>
      )}
      <Text c="dark.2" component="div" size="xs">
        <Group gap="xs">
          <IconInfoCircle size={16} />
          <Group gap="xs">
            AI responses may be inaccurate or misleading.
            <Anchor component={Link} to=".">
              Learn more
            </Anchor>
          </Group>
        </Group>
      </Text>
    </motion.div>
  );
}
